import {container, singleton} from "tsyringe";
import {FormController} from "../components/_reactive-controllers/form.controller";
import {ICommand} from "../commands/i-command.interface";
import {Issue} from "../domain/entities/issue.entity";
import {CreateIssueCommand} from "../commands/create-issue.command";
import {UpdateIssueCommand} from "../commands/update-issue.command";
import {EmployeeToIssueAssignment} from "../domain/entities/employee-to-issue-assignment.entity";
import {DeleteEmployeeToIssueAssignmentCommand} from "../commands/delete-employee-to-issue-assignment.command";
import {CreateEmployeeToIssueAssignmentCommand} from "../commands/create-employee-to-issue-assignment.command";
import {UpdateEmployeeToIssueAssignmentCommand} from "../commands/update-employee-to-issue-assignment.command";
import {EmployeeToIssueAssignmentRepository} from "../domain/repositories/employee-to-issue-assignment.repository";
import {ChangeTracker} from "../change-tracking/change-tracker";
import {IssueEmployeesHelper} from "../helpers/issue-employees.helper";

/**
 * Form data type - should match IssueFormService definition
 */
type FormData = {
    title: string;
    status: any;
    type: any;
    project: any;
    keywords: any[];
    timeRange: any;
    creator: any;
    description: any;
    employeeToIssueAssignments?: EmployeeToIssueAssignment[];
}

/**
 * Service for building Commands from Issue entities and form data.
 *
 * This service centralizes all command creation logic and implements
 * the Command Pattern for converting UI changes into backend operations.
 *
 * **Command Pattern Benefits:**
 * - Decouples UI (Dialog) from Business Logic (Commands)
 * - Makes operations testable in isolation
 * - Allows for undo/redo functionality in the future
 * - Centralizes complex business rules
 *
 * **SRP Principle**: Only responsible for command creation
 * **DRY Principle**: Command logic exists only once, not per dialog
 * **DIP Principle**: Depends on injected repositories, not direct resolution
 */
@singleton()
export class IssueCommandBuilderService {

    /**
     * Repository is resolved on demand to avoid circular dependencies.
     * Since repositories are not always registered as singletons, we resolve
     * them when needed rather than injecting them.
     */
    private get assignmentRepository(): EmployeeToIssueAssignmentRepository {
        return container.resolve(EmployeeToIssueAssignmentRepository);
    }

    /**
     * **MAIN PUBLIC API - KISS Principle**
     *
     * Builds all necessary commands for saving an Issue and its related data.
     * This replaces the scattered _convertIssueToCommands logic from the dialog.
     *
     * **What this method does:**
     * 1. Determines if Issue is new or existing
     * 2. Builds appropriate Create/Update commands
     * 3. Handles Employee Assignment changes
     * 4. Returns clean command list
     *
     * @param issue The Issue entity to save
     * @param formController Form controller containing user changes
     * @returns Array of commands to execute
     */
    async buildCommands(issue: Issue, formController: FormController<FormData>): Promise<ICommand[]> {
        const commands: ICommand[] = [];

        // **Strategy Pattern**: Different strategies for new vs existing Issues
        if (issue.isNew()) {
            commands.push(...this.buildCreateCommands(issue, formController));
        } else {
            commands.push(...this.buildUpdateCommands(issue, formController));
        }

        // **Separation of Concerns**: Assignment logic handled separately
        const assignmentResult = this.buildAssignmentCommands(issue, formController);
        commands.push(...assignmentResult.commands);

        // The dialog writes only the assignments; keep the derived employee list in step,
        // or views that place issues by it (/by-assignee) find no row for this issue: the
        // DTO transformer returns already-loaded issues unchanged after the refetch.
        if (assignmentResult.assignmentsWritten) {
            issue.applyDerivedEmployees(await IssueEmployeesHelper.fromAssignments(issue.employeeToIssueAssignments));
        }

        return commands;
    }

    /**
     * **SRP: Single Responsibility for Create Logic**
     *
     * Handles creation of new Issues. Separated from update logic
     * to make code easier to understand and maintain.
     *
     * **Why separate method?**
     * - Create logic is different from update logic
     * - Easier to test in isolation
     * - Clearer code organization
     *
     * @param issue New Issue entity
     * @param formController Form controller with user input
     * @returns Array of create commands
     */
    private buildCreateCommands(issue: Issue, formController: FormController<FormData>): ICommand[] {
        const commands: ICommand[] = [];

        // Get form values for validation
        const values = formController.form?.getValue();
        if (!values) {
            throw new Error('Form values are undefined');
        }

        // **Business Rule**: Employee assignments handled separately
        // This ensures clean separation between Issue and Assignment creation
        delete values?.employeeToIssueAssignments;

        Object.assign(issue, values);
        commands.push(new CreateIssueCommand(issue));

        return commands;
    }

    /**
     * **SRP: Single Responsibility for Update Logic**
     *
     * Handles updates to existing Issues. Only updates fields that
     * have actually changed to minimize backend operations.
     *
     * **Optimization**: Only dirty fields are updated
     * **Business Rule**: Assignments excluded (handled separately)
     *
     * @param issue Existing Issue entity
     * @param formController Form controller with changes
     * @returns Array of update commands
     */
    private buildUpdateCommands(issue: Issue, formController: FormController<FormData>): ICommand[] {
        const commands: ICommand[] = [];

        // **Performance Optimization**: Only extract changed fields
        const changes = this.extractChangedFields(formController);

        if (Object.keys(changes).length > 0) {
            // **Mutation Pattern**: Apply changes to entity
            Object.assign(issue, changes);
            commands.push(new UpdateIssueCommand(issue));
        }

        return commands;
    }

    /**
     * **Complex Business Logic Centralized**
     *
     * Handles Employee-to-Issue Assignment changes. This is the most complex
     * part because it needs to diff current vs. new assignments and create
     * appropriate Create/Delete commands.
     *
     * **Algorithm:**
     * 1. Get new assignments from form
     * 2. Compare with existing assignments
     * 3. Create Delete commands for removed assignments
     * 4. Create Create commands for new assignments
     *
     * @param issue Issue entity
     * @param formController Form controller
     * @returns The assignment commands, and whether the issue's assignments were
     *          replaced (false on both early returns) — the caller derives `employees` only then
     */
    private buildAssignmentCommands(issue: Issue, formController: FormController<FormData>): {commands: ICommand[], assignmentsWritten: boolean} {
        const commands: ICommand[] = [];

        const assignmentsField = formController.form
            ?.getField<EmployeeToIssueAssignment[], unknown>('employeeToIssueAssignments');
        if (!assignmentsField?.isValid) {
            return {commands, assignmentsWritten: false};
        }

        const newAssignments = assignmentsField.getValue() ?? [];

        // Dirtiness answers "did the user touch this field?", which is not the question this
        // guard needs to answer. A clone — and every row-header "+" that pre-fills an assignee —
        // opens the dialog with assignment rows already in place, so nothing about the field
        // changes, yet those rows have never been written. Asking the field alone dropped them
        // silently: the save sent create.issue (whose payload carries no assignments at all —
        // only the derived employee uids, which the server reads and does not store) and
        // nothing else, and the reopened issue had no assignee. `isNew` is the question, and the
        // create branch below already asks it — it just used to sit behind this guard.
        const hasUnpersistedAssignment = newAssignments.some(
            assignment => this.assignmentRepository.isNew(assignment)
        );
        if (!assignmentsField.isDirty && !hasUnpersistedAssignment) {
            return {commands, assignmentsWritten: false};
        }

        // **Diff Algorithm**: Find assignments to delete, update (budget change) and create
        commands.push(...this.buildAssignmentDeleteCommands(issue, newAssignments));
        commands.push(...this.buildAssignmentUpdateCommands(issue, newAssignments));
        commands.push(...this.buildAssignmentCreateCommands(issue, newAssignments));

        // **State Synchronization**: Update Issue entity with new assignments
        issue.employeeToIssueAssignments = newAssignments;

        return {commands, assignmentsWritten: true};
    }

    /**
     * **Utility Method: Extract Changed Form Fields**
     *
     * Iterates through form controls and extracts only the fields
     * that have been modified by the user.
     *
     * **Performance**: Avoids unnecessary backend calls
     * **Clean Code**: Descriptive method name explains intent
     *
     * @param formController Form controller
     * @returns Object with only changed fields
     */
    private extractChangedFields(formController: FormController<FormData>): Partial<Pick<Issue, keyof FormData>> {
        const changes: Partial<Pick<Issue, keyof FormData>> = {};

        // **Iteration Pattern**: Process each form field
        for (const field of formController.form?.children || []) {
            // **Business Rule**: Skip assignments (handled separately)
            // **Validation**: Only include dirty and valid fields
            if (field.key !== 'employeeToIssueAssignments' && field.isDirty && field.isValid) {
                const value = field.getValue();
                if (value !== undefined) {
                    // **Dynamic Assignment**: Use computed property names
                    changes[field.key as keyof FormData] = value;
                }
            }
        }

        return changes;
    }

    /**
     * **Assignment Diff Algorithm - Part 1: Deletions**
     *
     * Finds assignments that exist in the Issue but not in the new
     * assignment list, and creates Delete commands for them.
     *
     * @param issue Issue entity with current assignments
     * @param newAssignments New assignment list from form
     * @returns Array of delete commands
     */
    private buildAssignmentDeleteCommands(issue: Issue, newAssignments: EmployeeToIssueAssignment[]): ICommand[] {
        const commands: ICommand[] = [];

        // **Set Operations**: Get UIDs for efficient lookup
        const newUids = newAssignments.map(assignment => assignment.uid);

        // **Filter Pattern**: Find assignments to delete
        const entitiesToDelete = issue.employeeToIssueAssignments
            .filter(assignment => !newUids.includes(assignment.uid));

        // **Mapping Pattern**: Convert entities to commands
        commands.push(...entitiesToDelete.map(assignment =>
            new DeleteEmployeeToIssueAssignmentCommand('issue', issue.uid, assignment)
        ));

        return commands;
    }

    /**
     * **Assignment Diff Algorithm - Part 2: Creations**
     *
     * Finds assignments that are new (not yet persisted) and creates
     * Create commands for them.
     *
     * **Repository Pattern**: Uses injected repository to check if new
     *
     * @param issue Issue entity
     * @param newAssignments New assignment list from form
     * @returns Array of create commands
     */
    private buildAssignmentCreateCommands(issue: Issue, newAssignments: EmployeeToIssueAssignment[]): ICommand[] {
        const commands: ICommand[] = [];

        // **Repository Pattern**: Use injected dependency
        const entitiesToCreate = newAssignments.filter(
            assignment => this.assignmentRepository.isNew(assignment)
        );

        // **Mapping Pattern**: Convert entities to commands
        commands.push(...entitiesToCreate.map(assignment =>
            new CreateEmployeeToIssueAssignmentCommand('issue', issue.uid, assignment)
        ));

        return commands;
    }

    /**
     * **Assignment Diff Algorithm - Part 3: Updates (O2D-PRD-104)**
     *
     * Finds an assignment that survives on the SAME (employee, issue) pairing but
     * whose tracked `timeBudget` was mutated in place (kept its uid → the
     * ChangeTracker proxy is DIRTY). Such a change is ONE Update — not the historic
     * Delete + Create — which preserves the assignment's identity and lets the audit
     * trail group it as a single "Stundenkontingent geändert" business event.
     *
     * A genuine reassignment (different employee/issue → different pairing) is NOT
     * matched here: the new assignment is `isNew()` and the old uid is absent from
     * the surviving list, so it correctly falls to the delete/create paths.
     *
     * @param issue Issue entity with current assignments
     * @param newAssignments New assignment list from form
     * @returns Array of update commands (0 or more)
     */
    private buildAssignmentUpdateCommands(issue: Issue, newAssignments: EmployeeToIssueAssignment[]): ICommand[] {
        // **Set Operations**: uids that already existed (persisted pairings)
        const oldUids = issue.employeeToIssueAssignments.map(assignment => assignment.uid);

        // **Filter Pattern**: persisted + same pairing survives + only timeBudget changed
        const entitiesToUpdate = newAssignments.filter(assignment =>
            !this.assignmentRepository.isNew(assignment)
            && oldUids.includes(assignment.uid)
            && this.hasTimeBudgetChange(assignment)
        );

        // **Mapping Pattern**: Convert entities to commands
        return entitiesToUpdate.map(assignment =>
            new UpdateEmployeeToIssueAssignmentCommand('issue', issue.uid, assignment)
        );
    }

    /**
     * True when the tracked assignment is DIRTY specifically on `timeBudget` — the
     * sole trigger for an in-place Update rather than a Delete + Create. Reading the
     * ChangeTracker proxy keeps the discriminator tied to the compound-key mechanism
     * (unchanged employee/issue, only the hour budget moved).
     *
     * @param assignment Tracked assignment proxy from the form value
     * @returns Whether the assignment's timeBudget is a tracked change
     */
    private hasTimeBudgetChange(assignment: EmployeeToIssueAssignment): boolean {
        const tracker = (assignment as EmployeeToIssueAssignment & {
            changeTracker?: ChangeTracker<EmployeeToIssueAssignment>
        }).changeTracker;

        return !!tracker?.isDirty && tracker.getChangedPropertyKeys().includes('timeBudget');
    }
}